import React from 'react';
import MainLayout from '../layouts/MainLayout';
import { pageMeta } from '../lib/seo';
import { Card, CardContent } from '../components/Card';
import { Target, Users, Award, TrendingUp, Shield, Clock } from 'lucide-react';
import Button from '../components/Button';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

const AboutPage = () => {
  const values = [
    {
      icon: Shield,
      title: 'Zuverlässigkeit',
      description: 'Auf uns können Sie sich verlassen. Termingerecht, professionell und rechtssicher.'
    },
    {
      icon: Award,
      title: 'Qualität',
      description: 'Geprüfte und qualifizierte Mitarbeiter für höchste Qualitätsstandards.'
    },
    {
      icon: Clock,
      title: 'Flexibilität',
      description: 'Schnelle Verfügbarkeit und flexible Lösungen für Ihren individuellen Bedarf.'
    },
    {
      icon: Users,
      title: 'Partnerschaft',
      description: 'Langfristige Partnerschaften auf Augenhöhe sind unser Ziel.'
    }
  ];

  return (
    <MainLayout
      title={pageMeta.about.title}
      description={pageMeta.about.description}
      keywords={pageMeta.about.keywords}
    >
      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 md:pt-24 pb-8 bg-gradient-to-b from-primary-skin/30 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary-text-dark mb-6">
            Über SENKAY
          </h1>
          <p className="text-lg md:text-xl text-primary-text-muted leading-relaxed">
            Ihr zuverlässiger Partner für professionelle Personal- und Logistiklösungen
            in München, Bayern und der gesamten DACH-Region.
          </p>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16 md:pb-24">
        <div className="grid md:grid-cols-2 gap-12">
          <Card>
            <CardContent>
              <div className="w-14 h-14 mb-6 rounded-xl bg-primary-blue-light flex items-center justify-center">
                <Target className="w-7 h-7 text-primary-blue-mid" />
              </div>
              <h2 className="text-2xl font-bold text-primary-text-dark mb-4">
                Unsere Mission
              </h2>
              <p className="text-primary-text-muted leading-relaxed">
                Wir machen Personalbeschaffung einfach, schnell und zuverlässig. SENKAY steht
                für höchste Qualitätsstandards, transparente Prozesse und individuelle Lösungen.
                Unser Ziel ist es, die erste Wahl für Unternehmen zu sein, die auf professionelle
                Personaldienstleistungen setzen.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <div className="w-14 h-14 mb-6 rounded-xl bg-primary-green-light flex items-center justify-center">
                <TrendingUp className="w-7 h-7 text-green-700" />
              </div>
              <h2 className="text-2xl font-bold text-primary-text-dark mb-4">
                Unsere Vision
              </h2>
              <p className="text-primary-text-muted leading-relaxed">
                Wir wollen der führende Anbieter für Personal- und Logistiklösungen in der
                DACH-Region werden. Durch kontinuierliche Innovation, höchste Servicequalität
                und nachhaltige Partnerschaften schaffen wir Mehrwert für unsere Kunden und
                Mitarbeiter.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Values */}
      <section className="section-container bg-primary-skin/20">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-primary-text-dark mb-4">
            Unsere Werte
          </h2>
          <p className="text-primary-text-muted max-w-2xl mx-auto">
            Diese Prinzipien leiten unser tägliches Handeln und prägen unsere Unternehmenskultur.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {values.map((value, index) => (
            <Card key={index} className="text-center">
              <CardContent>
                <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-primary-blue-light flex items-center justify-center">
                  <value.icon className="w-7 h-7 text-primary-blue-mid" />
                </div>
                <h3 className="text-xl font-semibold text-primary-text-dark mb-3">
                  {value.title}
                </h3>
                <p className="text-sm text-primary-text-muted">
                  {value.description}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Team Section */}
      <section className="section-container bg-primary-skin/20">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-primary-text-dark mb-4">
            Unser Team
          </h2>
          <p className="text-primary-text-muted max-w-2xl mx-auto">
            Erfahrene Experten mit Leidenschaft für Personaldienstleistungen und Logistik.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {[
            {
              name: 'Team aus 2 Geschäftsführern',
              role: 'Geschäftsführung',
              description: 'Über 10 Jahre Erfahrung in Personaldienstleistungen und strategischer Unternehmensführung.'
            },
            {
              name: 'Team Recruiting',
              role: 'Personalbeschaffung',
              description: 'Spezialisiert auf die Auswahl und Qualifizierung von Fachkräften für alle Branchen.'
            },
            {
              name: 'Team Operations',
              role: 'Einsatzleitung',
              description: 'Koordiniert Einsätze und sorgt für reibungslose Abläufe vor Ort.'
            }
          ].map((member, index) => (
            <Card key={index}>
              <CardContent className="text-center">
                {/* Image Placeholder */}
                <div className="w-32 h-32 mx-auto mb-4 rounded-full bg-gradient-to-br from-primary-blue-light to-primary-blue-mid flex items-center justify-center">
                  <Users className="w-16 h-16 text-white" />
                </div>
                <h3 className="text-xl font-bold text-primary-text-dark mb-1">
                  {member.name}
                </h3>
                <p className="text-primary-blue-mid font-medium mb-3">
                  {member.role}
                </p>
                <p className="text-sm text-primary-text-muted">
                  {member.description}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-container">
        <Card>
          <CardContent className="text-center p-12">
            <h2 className="text-2xl md:text-3xl font-bold text-primary-text-dark mb-4">
              Überzeugt? Lassen Sie uns sprechen!
            </h2>
            <p className="text-primary-text-muted mb-6 max-w-2xl mx-auto">
              Kontaktieren Sie uns für ein unverbindliches Beratungsgespräch.
            </p>
            <Link to="/kontakt">
              <Button variant="primary" size="lg" icon={ArrowRight} iconPosition="right">
                Jetzt Kontakt aufnehmen
              </Button>
            </Link>
          </CardContent>
        </Card>
      </section>
    </MainLayout>
  );
};

export default AboutPage;
